/**
 * Lenis smooth scroll feature
 * Initializes and manages Lenis smooth scrolling
 */

import Lenis from 'lenis'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { DEFAULT_LENIS_CONFIG } from './constants'
import { areAnimationsEnabled } from '../../utils/animation-settings'
import type { LenisConfig, LenisInstance } from './types'
import type storyData from '../../config/story.json'

type StoryData = typeof storyData

function createNoopInstance(): LenisInstance {
  return { lenis: null, frameCallbacks: [], destroy: () => {} }
}

/**
 * iOS Chrome/Safari: address bar show/hide changes the viewport but often does not fire `window.resize`.
 * `visualViewport` does fire, but during an active touch drag / Lenis animation we MUST NOT
 * call `lenis.resize()` or `ScrollTrigger.refresh()` — that jumps the page (refresh re-applies
 * scroll through the `immediate: true` proxy, which looks like a snap to the bottom).
 *
 * Strategy:
 *  - Listen to `visualViewport.resize` and remember we need a sync.
 *  - Only flush the sync once the user is idle: no touch on screen, and Lenis has stopped.
 */
function setupVisualViewportSync(lenis: Lenis): () => void {
  const vv = window.visualViewport
  if (!vv) return () => {}

  let pending = false
  let touching = 0
  let idleTimer: number | null = null

  const canFlush = () => touching === 0 && !lenis.isScrolling
  const clearIdle = () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer)
      idleTimer = null
    }
  }

  const flush = () => {
    clearIdle()
    if (!pending) return
    if (!canFlush()) {
      scheduleFlush()
      return
    }
    pending = false
    lenis.resize()
    ScrollTrigger.refresh()
  }

  const scheduleFlush = () => {
    clearIdle()
    idleTimer = window.setTimeout(flush, 200)
  }

  const onViewportResize = () => {
    pending = true
    scheduleFlush()
  }

  const onTouchStart = () => {
    touching++
    clearIdle()
  }
  const onTouchEnd = () => {
    touching = Math.max(0, touching - 1)
    if (pending) scheduleFlush()
  }

  vv.addEventListener('resize', onViewportResize)
  window.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchend', onTouchEnd, { passive: true })
  window.addEventListener('touchcancel', onTouchEnd, { passive: true })

  return () => {
    clearIdle()
    vv.removeEventListener('resize', onViewportResize)
    window.removeEventListener('touchstart', onTouchStart)
    window.removeEventListener('touchend', onTouchEnd)
    window.removeEventListener('touchcancel', onTouchEnd)
  }
}

/**
 * Initialize Lenis smooth scroll with configuration from story data.
 * Returns a no-op instance when animations are disabled (native browser scroll is used instead).
 */
export function initLenis(storyData: StoryData): LenisInstance {
  if (!areAnimationsEnabled()) return createNoopInstance()

  const scrollSpeedCfg = storyData.config?.scrollSpeed as { base?: number; baseMobile?: number } | undefined
  const baseWheel = scrollSpeedCfg?.base ?? DEFAULT_LENIS_CONFIG.baseWheel
  const baseTouch = scrollSpeedCfg?.baseMobile ?? DEFAULT_LENIS_CONFIG.baseTouch

  return setupLenis({
    baseWheel,
    baseTouch,
  })
}

/**
 * Setup Lenis smooth scroll
 * @param config - Configuration options
 * @returns Lenis instance with frame callbacks
 */
export function setupLenis(config: Partial<LenisConfig> = {}): LenisInstance {
  const {
    baseWheel = DEFAULT_LENIS_CONFIG.baseWheel,
    baseTouch = DEFAULT_LENIS_CONFIG.baseTouch,
    duration = DEFAULT_LENIS_CONFIG.duration,
    easing = DEFAULT_LENIS_CONFIG.easing,
  } = config

  const frameCallbacks: (() => void)[] = []

  const lenis = new Lenis({
    duration,
    easing,
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
    wheelMultiplier: baseWheel,
    syncTouch: false,
    syncTouchLerp: DEFAULT_LENIS_CONFIG.syncTouchLerp,
    touchMultiplier: baseTouch,
    infinite: false,
  })

  let removeVisualViewportListeners: () => void = () => {}
  if (typeof window !== 'undefined') {
    ;(window as { __lenis?: Lenis }).__lenis = lenis
    setupScrollTriggerIntegration(lenis, frameCallbacks)
    removeVisualViewportListeners = setupVisualViewportSync(lenis)
  }

  startRafLoop(lenis, frameCallbacks)

  return {
    lenis,
    frameCallbacks,
    destroy: () => {
      removeVisualViewportListeners()
      frameCallbacks.length = 0
      lenis?.destroy()
    },
  }
}

function setupScrollTriggerIntegration(lenis: Lenis, frameCallbacks: (() => void)[]): void {
  ScrollTrigger.scrollerProxy(document.body, {
    scrollTop(...args: number[]) {
      if (args.length > 0) {
        lenis?.scrollTo(args[0], { immediate: true })
      }
      return lenis?.scroll ?? 0
    },
    getBoundingClientRect() {
      // Match Lenis Dimensions (inner*), updated when visualViewport sync calls lenis.resize().
      return {
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      }
    },
  })

  let scrollTimeout: number | null = null
  lenis.on('scroll', () => {
    ;(window as { __lenisScrolling?: boolean }).__lenisScrolling = true
    if (scrollTimeout) clearTimeout(scrollTimeout)
    scrollTimeout = window.setTimeout(() => {
      ;(window as { __lenisScrolling?: boolean }).__lenisScrolling = false
    }, 1000)
  })

  frameCallbacks.push(() => ScrollTrigger.update())
}

function startRafLoop(lenis: Lenis, frameCallbacks: (() => void)[]): void {
  function raf(time: number) {
    lenis?.raf(time)
    for (let i = 0; i < frameCallbacks.length; i++) {
      frameCallbacks[i]()
    }
    requestAnimationFrame(raf)
  }
  requestAnimationFrame(raf)
}
